import {
  ArrowDownIcon,
  Badge,
  Button,
  DesktopIcon,
  MessagesDefaultIcon,
  ProfileIcon,
  QuestionmarkCircleIcon,
} from '@bp/ui-components';
import classNames from 'classnames';
import { type FragmentType, getFragmentData, graphql } from 'client/gql';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { useTotalUnreadCount } from 'matrix/hooks/useNotifications';
import { type FC, useContext, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useNavigate } from 'react-router-dom';
import { DesktopContext } from '../../context/IsDesktop';
import { useAuthClaims } from '../../hooks/useAuthClaims';
import { RoomType } from '../../matrix/constants';
import { useMatrixStatus } from '../../matrix/hooks/useMatrixStatus';
import { AboutModal } from '../Modals/AboutModal/AboutModal';
import { HeaderMeetingsFlyout } from './components/Meetings/HeaderMeetingsFlyout';
import { NotificationsFlyout } from './components/Notifications/NotificationsFlyout';
import styles from './Header.module.scss';

const HeaderQueryFieldsDocument = graphql(`
  fragment HeaderQueryFields on Query {
    ...HeaderMeetingsFlyoutFields
  }
`);

type HeaderProps = {
  className?: string | undefined;
  query: FragmentType<typeof HeaderQueryFieldsDocument> | undefined;
};

export const Header: FC<HeaderProps> = ({ className, query }) => {
  const fragment = getFragmentData(HeaderQueryFieldsDocument, query);
  const { pimAuthClaims } = useAuthClaims();
  const organization = pimAuthClaims.getOrganization();
  const auth = useAuth();
  const perms = usePermissionChecker();

  const { showForceDesktopSwitch, toggleDesktopMode, forceDesktopMode } = useContext(DesktopContext);

  const navigate = useNavigate();
  const [showModal, setShowModal] = useState<boolean>(false);

  const { isReady } = useMatrixStatus();

  // Zähle nur ungelesene Nachrichten in DM-Räumen
  const unreadCount = useTotalUnreadCount({ includeRoomTypes: [RoomType.DM] });

  return (
    <>
      <div className={classNames(styles.header, className)}>
        <div className={styles['organisation-switch']} data-cy={'organizationSelect'}>
          {organization?.name && <div className={styles.name}>{organization.name}</div>}
          <Button
            hierarchy='tertiary'
            onClick={() => {
              auth.signinRedirect({
                prompt: ['select_account', 'consent'].join(' '),
              });
            }}
            icon={<ArrowDownIcon />}
          />
        </div>
        <div className={styles['fly-outs']}>
          <HeaderMeetingsFlyout query={fragment} />
          <Badge content={unreadCount} color={'var(--color-error)'} hidden={unreadCount === 0}>
            <Button onClick={() => navigate('/messages')} hierarchy='tertiary' icon={<MessagesDefaultIcon />} />
          </Badge>
          <NotificationsFlyout navigate={navigate} />
          <Button
            className={styles.about}
            hierarchy='tertiary'
            icon={<QuestionmarkCircleIcon onClick={() => setShowModal(true)} />}
          />
          {showForceDesktopSwitch && (
            <Button
              hierarchy='tertiary'
              onClick={() => toggleDesktopMode()}
              icon={<DesktopIcon className={forceDesktopMode ? styles['desktop-mode-active'] : ''} />}
            />
          )}
          <Button hierarchy='secondary' icon={<ProfileIcon />} onClick={() => navigate('/profile')}>
            {pimAuthClaims.getProfile().displayName}
          </Button>
        </div>
      </div>

      <AboutModal
        isAdmin={!!perms?.isOrganizationAdmin(organization)}
        matrixOnline={isReady}
        state={showModal}
        setState={(value) => setShowModal(value)}
      />
    </>
  );
};
